import { useForm } from "@tanstack/react-form";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { z } from "zod";
import { useEffect, useState } from "react";
import {
  Button,
  InputContainer,
  InputOption,
  InputSelect,
  InputNumber,
  InputCurrency,
} from "@sellar-io/components";
import { InputLabel } from "@sellar-io/components";
import { formatPriceInputToApi } from "../../../hooks/usePriceFormat";
import { PriceTierGetResponseType } from "@sellar-io/api-v2/types";
import { usePriceTierContainerDiscountUpdate } from "../../../hooks/api/v2/priceTierContainerDiscount/usePriceTierContainerDiscountUpdate";
import { usePriceTierContainerDiscountDelete } from "../../../hooks/api/v2/priceTierContainerDiscount/usePriceTierContainerDiscountDelete";
import { getContainerTypeLabel } from "./containerDiscountOptions";

export const ContainerDiscountUpdateDialog = ({
  active,
  setActive,
  discount,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  discount: PriceTierGetResponseType["PriceTierContainerDiscounts"][number];
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const onDismiss = () => {
    setActive(false);
  };
  const formSchema = z.object({
    derivationType: z.enum(["flat", "percentage"]),
    derivationValue: z.number().min(0),
    containerType: z.enum([discount.containerType]),
  });
  const form = useForm({
    defaultValues: {
      derivationType: discount.derivationType,
      derivationValue: discount.derivationValue,
      containerType: discount.containerType,
    },
    onSubmit: ({ value }) => {
      handleCreateContainerDiscount(formSchema.parse(value));
    },
  });

  const priceTierContainerDiscountUpdateMutation =
    usePriceTierContainerDiscountUpdate(discount.id);

  const priceTierContainerDiscountDeleteMutation =
    usePriceTierContainerDiscountDelete(discount.id);

  const handleCreateContainerDiscount = ({
    derivationType,
    derivationValue,
    containerType,
  }: z.infer<typeof formSchema>) => {
    setErrorMessage("");

    priceTierContainerDiscountUpdateMutation.mutate(
      {
        derivationType,
        derivationValue,
        containerType,
      },
      {
        onSuccess: () => {
          onDismiss();
        },
        onError: (error) => {
          const message =
            apiV2AssertError(error)?.response?.data.message ??
            "Unable to update container discount";
          setErrorMessage(message);
        },
      },
    );
  };

  const handleDelete = () => {
    priceTierContainerDiscountDeleteMutation.mutate(undefined, {
      onSuccess: () => {
        onDismiss();
      },
      onError: (error) => {
        const message =
          apiV2AssertError(error)?.response?.data.message ??
          "Unable to delete container discount";
        setErrorMessage(message);
      },
    });
  };

  const getContainerText = () => getContainerTypeLabel(discount.containerType);

  useEffect(() => {
    form.setFieldValue("containerType", discount.containerType);
    form.setFieldValue("derivationType", discount.derivationType);
    form.setFieldValue("derivationValue", discount.derivationValue);
  }, [discount, form]);

  const loading =
    priceTierContainerDiscountDeleteMutation.isPending ||
    priceTierContainerDiscountUpdateMutation.isPending;

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title="Update top level discount"
        description="Any product item with their price rule set to 'Inherit' will have this discount applied"
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {errorMessage && (
          <Alert
            type="error"
            message={
              <span className="relative -top-0.5 text-sm">{errorMessage}</span>
            }
            className="w-full text-sm"
          />
        )}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <form.Field name="derivationType">
                {({ state, handleBlur, handleChange }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <InputLabel>Discount type</InputLabel>
                      <InputContainer>
                        <InputSelect
                          defaultValue={state.value}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const newType = e.target.value as
                              | "percentage"
                              | "flat";
                            handleChange(newType);

                            // Reset the derivationValue field appropriately
                            if (newType === "percentage") {
                              form.setFieldValue("derivationValue", 0); // reset or keep valid
                            } else if (newType === "flat") {
                              form.setFieldValue("derivationValue", 0); // or maybe keep same logic
                            }
                          }}
                        >
                          <InputOption value="percentage">
                            Percentage
                          </InputOption>
                          <InputOption value="flat">Flat</InputOption>
                        </InputSelect>
                      </InputContainer>
                    </div>
                  );
                }}
              </form.Field>
              <form.Field name="derivationValue">
                {({ state, handleBlur, handleChange }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <InputLabel>
                        Discount amount{" "}
                        {form.getFieldValue("derivationType") === "percentage"
                          ? "(%)"
                          : "(£)"}
                      </InputLabel>
                      <InputContainer>
                        {form.getFieldValue("derivationType") ===
                          "percentage" && (
                          <>
                            <span>%</span>
                            <InputNumber
                              defaultValue={state.value}
                              onBlur={handleBlur}
                              min={0}
                              max={100}
                              step={0.1}
                              onChange={(e) =>
                                handleChange(parseFloat(e.target.value))
                              }
                            />
                          </>
                        )}
                        {form.getFieldValue("derivationType") === "flat" && (
                          <InputCurrency
                            showPrefix
                            allowNegative
                            defaultValue={state.value}
                            onBlur={handleBlur}
                            onChange={(e) =>
                              handleChange(
                                formatPriceInputToApi(e.target.value) ?? 0,
                              )
                            }
                          />
                        )}
                      </InputContainer>
                    </div>
                  );
                }}
              </form.Field>
              <form.Field name="containerType">
                {() => {
                  return (
                    <div className="flex flex-col gap-1">
                      <InputLabel>Container type</InputLabel>
                      <InputContainer>
                        <InputSelect disabled={true} className="text-zinc-400">
                          <InputOption>{getContainerText()}</InputOption>
                        </InputSelect>
                      </InputContainer>
                    </div>
                  );
                }}
              </form.Field>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              color="red"
              type="button"
              loading={loading}
              onClick={handleDelete}
            >
              Delete
            </Button>
            <Button type="submit" loading={loading}>
              Update
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
